import React, { useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import classNames from 'classnames';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { toast } from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';

import {
    useProceduresUIStore, useProcedureUIStore,
    useUserPermissionsUIStore
} from '@core/useStores';
import { DATE_MMM_DD_YYYY_HH_MM } from '@shared/constants';
import { Table } from '@shared/components/Table';
import { SearchForm } from '@shared/components/SearchForm';
import { NoResultsView } from '@shared/components/NoResultsView';
import { ExpandableList } from '@shared/components/ExpandableList';
import ArrowRightIcon from '@assets/arrow-right.svg';
import EditIcon from '@assets/edit.svg';

import { ConfirmationModal } from '@shared/components/ConfirmationModal';
import { ToastMessage } from '@shared/components/Toast';

import { IPageQueryStoreSnapshotIn, IProcedureDevice } from 'Procedures/stores';
import { ProceduresFiltersModal } from '../ProceduresFiltersModal';
import { ProceduresFiltersBar } from '../ProceduresFiltersBar';
import { NoProceduresInGroup } from '../NoProceduresInGroup';
import { ProceduresSummary } from '../ProceduresSummary';
import { ProcedureModal } from '../ProcedureModal';
import { ProceduresExport } from '../ProceduresExport';
import { VersionUploadModal } from '../VersionUploadModal';

import { useStyles } from './ProceduresPage.styles';
import { messages } from 'Procedures/procedures.messages';
import { ThemeProps } from '@styles/theme';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { IProcedureStore, IProcedureVersionStore } from 'Procedures/stores/Procedure.store';
import { DeviceIcon } from '@shared/components/DeviceIcon';
import { Button } from '@shared/components/Button';
import { Tooltip } from '@mui/material';
import { IStoresEnv } from '@core/storesEnv';
import { getEnv } from 'mobx-state-tree';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { ExecutionModes } from '@shared/components/ExecutionModes';
import NeoIcon from '@assets/competencies-icon.svg';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '@core/routes/RoutePaths';

type SubComponentProps = {
    row: {
        original: {
            subRows: Array<{
                description: string,
                access: {
                    id: string,
                    name: string,
                }[],
                devices: {
                    id: string,
                    name: string,
                }[],
                history: {
                    version: string,
                    date: string,
                    description: string,
                }[],
            }>,
        }
    }
}

type FetchDataType = {
    pageIndex: number;
    sortBy: {
        id: string;
        desc: boolean;
    }[]
}

type ProcedureMedia = {
    src: string;
    description: string;
}

type DeviceType = {
    type: string | null;
    platform: string | null;
    environments: string | null;
}

const PROCEDURE_TYPE_LABELS: Record<string, string> = {
    VT: 'Virtual Training',
    OpS: 'Operating Scenario',
};

const EXPANDER_CELL_WIDTH = 24;

const NoInfo = () => {
    const styles = useGridStyles();

    return <div className={styles.noInfo}>{messages['proceduresPage.noInfo']}</div>
}

const getProcedureTypeLabel = (value: string | null) => {
    if (!value) {
        return '';
    }

    return PROCEDURE_TYPE_LABELS[value] || value;
}

const columnHelper = createColumnHelper<IProcedureStore>();

const getColumnsData = (options: { hideDeviceLabels: boolean; compactModes: boolean; compactActions: boolean; mobileOnly: boolean }) => [
    columnHelper.display({
        id: 'expander',
        meta: { cardRole: 'hidden' },
        minSize: 40,
        maxSize: 40,
        header: () => (
            <div style={{ minWidth: EXPANDER_CELL_WIDTH }} />
        ),
        cell: ({ cell: { row } }) => {
            const styles = useGridStyles();
            const expanderHandler = row.getToggleExpandedHandler();

            return (
                <div
                    style={{
                        minWidth: EXPANDER_CELL_WIDTH,
                        display: 'flex',
                    }}
                    onClick={expanderHandler}
                >
                    {row.getCanExpand() && (
                        <img
                            src={ArrowRightIcon}
                            className={classNames(styles.expandIcon, { [styles.expanded]: row.getIsExpanded() })}
                        />
                    )}
                </div>
            )
        },
        size: 5,
        enableSorting: false,
    }),
    columnHelper.accessor('name', {
        header: messages['proceduresPage.table.header.name'],
        meta: { cardRole: 'title' },
        size: options.mobileOnly ? 42 : options.compactActions ? 28 : 24,
        cell: ({ cell: { row }, getValue }) => {
            const styles = useGridStyles();
            const { procedurePreview } = row.original as {
                procedurePreview: ProcedureMedia
            };
            const media = procedurePreview;

            return (
                <div className={styles.nameWrapper}>
                    {
                        media
                            ? (
                                <img
                                    className={styles.procedureImage}
                                    src={media.src}
                                    alt={media.description}
                                />
                            )
                            : (
                                <div className={styles.procedureImage}>
                                    <DescriptionIcon color='primary'/>
                                </div>
                            )
                    }

                    <span className={styles.procedureName}>{getValue()}</span>
                </div>
            );
        },
    }),
    columnHelper.display({
        id: 'devices',
        header: messages['proceduresPage.table.header.devices'],
        meta: { cardRole: 'chip' },
        size: options.mobileOnly ? 18 : options.compactActions ? 18 : 20,
        // enableSorting: false,
        cell: ({ cell: { row } }) => {
            const { versions } = row.original;

            const devices: DeviceType[] = versions.flatMap((version: IProcedureVersionStore) => version.devices.map((d: IProcedureDevice) => ({
                type: d.device,
                platform: d.platform,
                environments: d.environments.join('|'),
            })));
            const values = _.uniqBy(devices, device => device.platform);

            if (!values.length) {
                return <NoInfo />;
            }

            return (
                <div style={{
                    display: 'flex',
                }}>
                    {
                        values.map((device: DeviceType, index: number) =>
                            <DeviceIcon
                                key={index}
                                platform={device.platform ?? ''}
                                environments={device.environments}
                                id={index}
                                showLabel={!options.hideDeviceLabels}
                            />
                        )
                    }
                </div>
            );
        },
    }),
    columnHelper.accessor('availableModes', {
        header: messages['proceduresPage.table.header.availableModes'],
        meta: { cardRole: 'chip' },
        size: options.mobileOnly ? 24 : options.compactModes ? 26 : 24,
        cell: ({ getValue }) => {
            const value = getValue();
            if (!value.length) {
                return <NoInfo />;
            }

            return (
                <ExecutionModes
                    modes={value}
                    size={options.mobileOnly ? 'small' : options.compactModes ? 'normal' : 'large'}
                />
            );
        },
    }),
    columnHelper.accessor('type', {
        header: messages['proceduresPage.table.header.type'],
        meta: { cardRole: 'chip' },
        size: options.mobileOnly ? 8 : 6,
        minSize: 0,
        cell: ({ getValue }) => {
            const styles = useGridStyles();
            const value = getValue();

            if (!value) {
                return <NoInfo />;
            }

            return (
                <Tooltip title={getProcedureTypeLabel(value)} arrow>
                    <span className={styles.typeBadge}>{value}</span>
                </Tooltip>
            );
        },
    }),
    // {
    //   header: messages['proceduresPage.table.header.collaboration'],
    //   accessor: 'collaboration',
    //   size: 14,
    //   maxSize: 150,
    //   cell: ({ getValue }) => {
    //     return value ? messages['proceduresPage.available'] : messages['proceduresPage.notAvailable'];
    //   },
    // },
    columnHelper.accessor('executionsCount', {
        header: messages['proceduresPage.table.header.executionsCount'],
        meta: { cardRole: 'chip' },
        size: options.mobileOnly ? 7 : 4,
        minSize: 0,
        cell: ({ getValue }) => {
            const styles = useGridStyles();

            if (options.mobileOnly) {
                return (
                    <span
                        className={styles.executionsChip}
                        title={messages['proceduresPage.table.header.executionsCount']}
                        aria-label={messages['proceduresPage.table.header.executionsCount']}
                    >
                        <PlayCircleOutlineIcon fontSize="small" />
                        {getValue()}
                    </span>
                );
            }

            return <div className={styles.counter}>{getValue()}</div>;
        },
    }),
    columnHelper.display({
        id: 'actions',
        header:  messages['proceduresPage.table.header.actions'],
        meta: { cardRole: 'actions' },
        minSize: options.mobileOnly ? 124 : 136,
        size: options.mobileOnly ? 20 : options.compactActions ? 16 : 12,
        maxSize: options.mobileOnly ? 148 : 160,
        enableSorting: false,
        cell: ({ cell: { row } }) => {
            const styles = useGridStyles();

            const procedureUIStore = useProcedureUIStore();
            const userPermissionsUIStore = useUserPermissionsUIStore();
            const navigate = useNavigate();
            const original: IProcedureStore = row.original;

            const openVersionUploadModal = () => {
                procedureUIStore.setEditProcedureId(original.id);
                procedureUIStore.toggleVersionUploadModalOpen(true);
            };

            const onCellClick = () => {
                procedureUIStore.setEditProcedureId(original.id);
                procedureUIStore.toggleProcedureModalOpen(true);
            }
            const defaultProcedure = original.companyId == "00000000-0000-0000-0000-000000000000" && !userPermissionsUIStore.isCompaniesAvailable;

            return (
                <span className={styles.procedureActions}>
                    <button
                        onClick={() => navigate(`/${RoutePaths.procedures}/analytics/${original.id}`, {
                            state: { procedureImage: (original as any).procedurePreview?.src ?? null },
                        })}
                        className={styles.button}
                        title="View executions"
                    >
                        <img src={NeoIcon} style={{ width: 22, height: 22 }} />
                    </button>
                    <button
                        onClick={openVersionUploadModal}
                        className={styles.button}
                        title={messages['procedureModal.versionUpload.title']}
                        disabled={defaultProcedure || !userPermissionsUIStore.canUpdateProcedure}
                    >
                        <CloudUploadOutlinedIcon color="primary" style={{ fontSize: 22 }} />
                    </button>
                    <button
                        onClick={() => (userPermissionsUIStore.canUpdateProcedure || defaultProcedure) && onCellClick()}
                        disabled={defaultProcedure || !userPermissionsUIStore.canUpdateProcedure}
                        className={styles.button}
                        title={messages['proceduresPage.editProcedure']}
                    >
                        <img src={EditIcon} />
                    </button>
                </span>
            )

        },
    }),
]

const useGridStyles = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    return styles;
}

export const ProceduresPage: React.FC = () => {
    const userPermissionsUIStore = useUserPermissionsUIStore();
    const proceduresGridUIStore = useProceduresUIStore();
    const procedureUIStore = useProcedureUIStore();

    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isMobileLayout = useBreakpointDown('mobile');
    const isTabletLayout = useBreakpointDown('tablet');
    const isCompactLayout = useBreakpointDown('desktop');

    const { filtersOptions } = getEnv<IStoresEnv>(proceduresGridUIStore);
    const [filtersModalOpen, toggleFiltersModalOpen] = useState(false);

    const initialSortState = {
        sorting: [{
            id: 'name',
            desc: false,
        }],
    };

    const columns: ColumnDef<IProcedureStore, any>[] = React.useMemo(() => {
        const baseColumns = getColumnsData({
            hideDeviceLabels: isTabletLayout,
            compactModes: isCompactLayout,
            compactActions: isCompactLayout,
            mobileOnly: isMobileLayout,
        });

        // Below 'mobile' the Table renders cards, which lay out every column
        // via its cardRole -- no need to drop columns there.
        if (isMobileLayout || !isCompactLayout) {
            return baseColumns;
        }

        const hiddenColumns = ['type', 'executionsCount'];

        return baseColumns.filter((column: any) => !hiddenColumns.includes(column.id ?? column.accessorKey));
    }, [isCompactLayout, isMobileLayout, isTabletLayout]);

    // Add state for version options and assignments

    const setGroupFilter = (inGroup: number) => {
        proceduresGridUIStore.setParams({
            page: 0,
            inGroup,
            inGroupId: undefined,
        });

        //groups.unselectGroup();
    }

    useEffect(() => {
        setGroupFilter(0);

        proceduresGridUIStore.cleanUp();
        proceduresGridUIStore.togglePageActivity(true);

        proceduresGridUIStore.load();
        proceduresGridUIStore.loadStatistics();
        filtersOptions.loadFilterOptions();
        setTimeout(() => { proceduresGridUIStore.togglePageActivity(true) }, 100);

        return () => {
            proceduresGridUIStore.togglePageActivity(false);
            proceduresGridUIStore.cleanUp();
        }
    }, []);

    const onFetchData = ({ pageIndex, sortBy }: FetchDataType) => {
        const params: IPageQueryStoreSnapshotIn = { page: pageIndex };

        if (sortBy && sortBy.length) {
            params.sortFieldName = sortBy[0].id;
            params.sortDirection = Number(sortBy[0].desc);
        }
        if (proceduresGridUIStore.isActivePage) { proceduresGridUIStore.setParams(params); }
    };

    const onSearchTextChanged = (searchText: string) => {
        if (searchText && searchText.length === 1) {
            return;
        }

        proceduresGridUIStore.setParams({
            page: 0,
            searchText,
        });
    };

    const onFiltersModalOpen = () => toggleFiltersModalOpen(true);
    const onFiltersModalClose = () => toggleFiltersModalOpen(false);

    const renderRowSubComponent = React.useCallback(({ row }: SubComponentProps) => {
        const data = row.original.subRows[0];

        return (
            <div className={styles.body}>
                <div className={styles.descriptionWrapper}>
                    <h5 className={styles.header}>{messages['proceduresPage.subRow.title']}</h5>
                    <div className={styles.text}>
                        {data.description || <NoInfo />}
                    </div>
                </div>

                {/* <div className={styles.devicesWrapper}>
                    <h5 className={styles.header}>{messages['proceduresPage.subRow.devices']}</h5>
                    <div className={styles.text}>
                        {
                            data.devices.length
                                ? <ExpandableList list={_.uniqBy(data.devices, d => d.name)} valueName='platform' />
                                : <NoInfo />
                        }
                    </div>
                </div> */}

                <div className={styles.accessWrapper}>
                    <h5 className={styles.header}>{messages['proceduresPage.subRow.access']}</h5>
                    <div className={styles.text}>
                        {
                            data.access.length
                                ? <ExpandableList list={data.access} />
                                : <NoInfo />
                        }
                    </div>
                </div>

                <div className={styles.historyWrapper}>
                    <h5 className={styles.header}>{messages['proceduresPage.subRow.history']}</h5>
                    <div className={styles.text}>
                        {
                            data.history.length
                                ? <ExpandableList list={data.history} />
                                : <NoInfo />
                        }
                    </div>
                </div>
            </div>
        )
    }, []);

    const closeProcedureModal = () => {
        procedureUIStore.toggleProcedureModalOpen(false);
        procedureUIStore.cleanUpProcedureModal();
    };

    const onConfirmationDecline = () => {
        procedureUIStore.toggleDeleteConfirmationModalOpened(false);
        procedureUIStore.toggleProcedureModalOpen(false);
        procedureUIStore.cleanUpProcedureModal();
    };

    const onDeleteConfirm = async () => {
        const result = await procedureUIStore.deleteProcedure();

        if (result.success) {
            toast.success(
                <ToastMessage
                    message={messages['proceduresPage.delete.success.toast']}
                    type='success'
                />
            );

            proceduresGridUIStore.setParams({ page: 0 });
            proceduresGridUIStore.load();
            proceduresGridUIStore.loadStatistics();
        }

        procedureUIStore.toggleDeleteConfirmationModalOpened(false);
        procedureUIStore.cleanUpProcedureModal();
    }

    const closeVersionUploadModal = () => {
        procedureUIStore.toggleVersionUploadModalOpen(false);
        procedureUIStore.cleanUpProcedureModal();
    };

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.tableActionsContainer}>
                <div className={styles.tableActions}>
                    <div className={styles.filters}>
                        <Observer>
                            {() => (
                                <>
                                    <SearchForm
                                        initialValues={{
                                            search: proceduresGridUIStore.requestParams.getStorageParams
                                                ? proceduresGridUIStore.requestParams.getStorageParams.searchText
                                                : '',
                                        }}
                                        onSearchTextChanged={onSearchTextChanged}
                                        className={styles.searchWrapper}
                                    />
                                </>
                            )}
                        </Observer>

                        <span style={{ display: 'inherit' }} title={messages['proceduresPage.filter.title']}>
                            <FilterListOutlinedIcon
                                onClick={onFiltersModalOpen}
                                className={styles.filterIcon}
                            />
                        </span>
                    </div>
                    <div className={styles.summaryPanel}>
                        <ProceduresSummary className={styles.summaryPanel} />
                    </div>

                    <div className={styles.buttonsWrapper}>
                        <div className={styles.invite}>
                            <Button variant="contained"
                                color="primary"
                                startIcon={<NoteAddIcon />}
                                disabled={!userPermissionsUIStore.canCreateProcedure}
                                onClick={() => procedureUIStore.toggleProcedureModalOpen(true)}>
                                {messages['proceduresPage.addProcedure.title']}
                            </Button>
                        </div>
                        <div className={classNames(styles.exportButton)}>
                            <ProceduresExport />
                        </div>
                    </div>
                    <ProceduresFiltersModal isOpen={filtersModalOpen} onRequestClose={onFiltersModalClose} />
                </div>
            </div>

            <ProceduresFiltersBar />
            <Observer>
                {() => {
                    const data = proceduresGridUIStore.procedures.toJSON();
                    const preparedData = data.map(row => ({
                        ...row,
                        subRows: [{
                            description: row.description,
                            devices: row.versions.flatMap(v => v.devices),
                            access: [...row.groups, ...row.users],
                            history: row.versions ? row.versions.map(version => ({
                                name: `${version.version} - ${moment(version.date).format(DATE_MMM_DD_YYYY_HH_MM)}${version.description ? " - " + version.description : ""}`,
                            })) : null,
                        }],
                    }));

                    return (
                        <>
                            { procedureUIStore.procedureModalOpened && (
                                <ProcedureModal
                                    isOpen={true}
                                    onRequestClose={() => closeProcedureModal()}
                                />
                            ) }

                            { procedureUIStore.versionUploadModalOpened && (
                                <VersionUploadModal
                                    isOpen={true}
                                    onRequestClose={closeVersionUploadModal}
                                />
                            ) }

                            {
                                procedureUIStore.deleteConfirmationModalOpened && (
                                    <ConfirmationModal
                                        isOpen={true}
                                        message={messages['proceduresPage.delete.message']}
                                        confirmText={messages['proceduresPage.delete.confirmText']}
                                        onDecline={onConfirmationDecline}
                                        onConfirm={() =>
                                            procedureUIStore.procedureToEditId && onDeleteConfirm()
                                        }
                                    />
                                )
                            }

                            {
                                proceduresGridUIStore.isTableVisible &&
                                <div className={styles.tableContainer}>
                                    <Table
                                        data={preparedData}
                                        columns={columns}
                                        fetchData={onFetchData}
                                        isLoading={proceduresGridUIStore.status.isLoading}
                                        useSkeleton={true}
                                        renderRowSubComponent={isMobileLayout ? undefined : renderRowSubComponent}
                                        paginationStatus={{ ...proceduresGridUIStore.pagination }}
                                        initialState={initialSortState}
                                    />
                                </div>
                            }

                            {
                                proceduresGridUIStore.noFilteringResults && <NoResultsView entityName='procedures' />
                            }

                            {
                                proceduresGridUIStore.noItemsInGroup && <NoProceduresInGroup />
                            }

                            {/* VersionAssignmentModal and ScoreDataModal are now embedded inside ProcedureModal edit mode */}
                        </>
                    );
                }}
            </Observer>
        </div>
    )
}
